import { ofType } from 'redux-observable';
import { merge, partition } from 'rxjs';
import { ignoreElements, mergeMap, pluck, tap } from 'rxjs/operators';
import { openedLostLeadModalAnalytics } from '@bridebook/web/lib/lost-lead/analytics/actions';
import { IEpic, TUIShortlistSupplier, VenueConfirmSlides } from '@bridebook/web/lib/types';
import {
  ConfirmSupplierInterestAction,
  DenySupplierInterestAction,
  SaveSupplierIdToConfirmAction,
  VenueConfirmActionTypes,
  VenueConfirmChangeSlideAction,
} from '@bridebook/web/lib/venue-confirmation/action-types';
import {
  closeBookingConfirmationModal,
  saveSupplierIdToConfirm,
  venueConfirmChangeSlide,
} from '@bridebook/web/lib/venue-confirmation/actions';
import { confirmOrDenyInterestAnalytics } from '@bridebook/web/lib/venue-confirmation/analytics/actions';

type InputActions = ConfirmSupplierInterestAction | DenySupplierInterestAction;
type OutputActions = SaveSupplierIdToConfirmAction | VenueConfirmChangeSlideAction | any; // because of thunks returned

/**
 * An Epic that handles confirming or denying supplier interest.
 */
export const confirmOrDenySupplierInterestEpic: IEpic<InputActions, OutputActions> = (
  action$,
  { lostLeadService },
) => {
  const [confirm$, denies$] = partition<InputActions>(
    action$.pipe(
      ofType(
        VenueConfirmActionTypes.CONFIRM_SUPPLIER_INTEREST,
        VenueConfirmActionTypes.DENY_SUPPLIER_INTEREST,
      ),
    ),
    isConfirmation,
  );

  return merge(
    merge(confirm$, denies$).pipe(
      pluck<InputActions, TUIShortlistSupplier>('payload'), // Extracts the 'payload' property from actions
      tap((supplier) => {
        lostLeadService.recordInteraction(supplier.id);
      }),
      ignoreElements(),
    ),
    // Handle confirmations
    confirm$.pipe(
      pluck<InputActions, TUIShortlistSupplier>('payload'),
      mergeMap((supplier) => [
        closeBookingConfirmationModal(),
        saveSupplierIdToConfirm(null),
        confirmOrDenyInterestAnalytics(true, supplier),
      ]),
    ),
    // Handle denies
    denies$.pipe(
      pluck<InputActions, TUIShortlistSupplier>('payload'),
      mergeMap((supplier) => [
        venueConfirmChangeSlide(VenueConfirmSlides.lostLead),
        openedLostLeadModalAnalytics('venueSpecificPopup', supplier),
        confirmOrDenyInterestAnalytics(false, supplier),
      ]),
    ),
  );
};

const isConfirmation = ({ type }: InputActions) =>
  type === VenueConfirmActionTypes.CONFIRM_SUPPLIER_INTEREST;
